/* Admin/CoE Operations Overview.
 *
 * iter100.190 — Portal grammar sweep. Rebuilt to match the Doctor
 * Dashboard visual grammar (iter100.189):
 *   • H1 unified to `text-2xl font-bold` (was `text-3xl sm:text-4xl` +
 *     "Control center" overline).
 *   • Every section header uses a shared `SectionHeader` primitive
 *     that sits ABOVE its content card, not inside. Same pattern the
 *     Doctor Dashboard uses.
 *   • Cards standardised to `p-4 rounded-xl` (was mixed p-5/p-6 +
 *     rounded-2xl/xl drift).
 *   • Icon colours moved from raw HSL strings to Tailwind tone tokens.
 *   • KPI stat-card palette collapsed to 3 semantic tones (primary /
 *     amber = needs-action / rose = urgent) instead of 8 arbitrary
 *     chromatics.
 *
 * External surfaces intentionally NOT touched in this sweep:
 *   • PlatformQualityWidget, AdminComplianceHeatmap, AdminDemoDataPanel
 *     — their own components with their own grammar drift. Future sweep.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
    Users, Stethoscope, Calendar, ClipboardText, UserList, Envelope,
    Briefcase, TrendUp, UserPlus, ChatCircleDots, Pulse, Clock,
    Tray, GraduationCap, Newspaper, ArrowRight, Warning,
    Camera, Gift,
} from "@phosphor-icons/react";
import AdminComplianceHeatmap from "@/components/admin/AdminComplianceHeatmap";
import AdminSectionShell from "@/components/admin/AdminSectionShell";
import AdminTicker from "@/components/admin/AdminTicker";
import useAdminActivityStream from "@/hooks/useAdminActivityStream";
import PlatformQualityWidget from "@/components/admin/PlatformQualityWidget";
import ReferralPipelineWidget from "@/components/admin/ReferralPipelineWidget";
import ServicesAdoptionWidget from "@/components/admin/ServicesAdoptionWidget";

/* iter100.190 — KPI palette collapsed to 3 tones:
     primary = stable count · amber = needs-action · rose = urgent.
   Icon colour swaps from raw HSL to Tailwind tokens.
   iter100.243 — Added `teal` for content-surface KPIs (Academy, Blog)
   and `violet` for the growth KPI (Referrals) so the 3 new groupings
   stay visually distinct from the operational band without ballooning
   the palette. */
const TONE_ICON = {
    primary: "text-primary",
    amber:   "text-amber-600",
    rose:    "text-rose-600",
    teal:    "text-teal-600",
    violet:  "text-violet-600",
};

/* iter100.243 — Consolidated KPI catalog. 12 tiles across 3 lenses.
   Order = "who touched the platform first" → community → content:
     1. Acquisition (Leads, Messages, Provider apps, Pending review)
     2. Community  (Patients, Providers, Cases, Appointments)
     3. Product    (Scan Visits, Referrals, Courses, Blog posts)
   Each card owns its href — do NOT re-key against SOURCE_META (that
   dictionary is for activity-feed items, keyed differently).
   Each card also carries a `sub` label — the tiny hint under the
   number that mirrors Analytics's Metric component grammar. */
const STAT_CARDS = [
    // Acquisition
    { key: "leads",                icon: UserList,     label: "Leads",          sub: "Web enquiries",       tone: "amber",   href: "/admin/leads?tab=web" },
    { key: "messages",             icon: Envelope,     label: "Messages",       sub: "Contact form",        tone: "amber",   href: "/admin/messages" },
    { key: "applications_total",   icon: Briefcase,    label: "Prov. apps",     sub: "Doctor onboarding",   tone: "primary", href: "/admin/leads?tab=application" },
    { key: "applications_pending", icon: TrendUp,      label: "Pending",        sub: "Awaiting decision",   tone: "rose",    href: "/admin/leads?tab=application" },
    // Community
    { key: "patients",             icon: Users,        label: "Patients",       sub: "Active accounts",     tone: "primary", href: "/admin/patients" },
    { key: "doctors",              icon: Stethoscope,  label: "Providers",      sub: "Active doctors",      tone: "primary", href: "/admin/providers" },
    { key: "cases",                icon: ClipboardText,label: "Cases",          sub: "All-time",            tone: "primary", href: "/admin/cases" },
    { key: "appointments",         icon: Calendar,     label: "Appts",          sub: "Bookings",            tone: "primary", href: "/admin/appointments" },
    // Product surfaces (new — iter100.243)
    { key: "scan_visits",          icon: Camera,       label: "IO Scans",       sub: "By Invisique",        tone: "teal",    href: "/admin/scan-visits" },
    { key: "referrals",            icon: Gift,         label: "Referrals",      sub: "Doctor pipeline",     tone: "violet",  href: "/admin/leads?tab=referrals" },
    { key: "courses_published",    icon: GraduationCap,label: "Courses",        sub: "Published",           tone: "teal",    href: "/admin/approvals?tab=courses" },
    { key: "blog_posts_published", icon: Newspaper,    label: "Blog posts",     sub: "Published",           tone: "teal",    href: "/admin/blog" },
];

/* iter100.243 — Activity-feed source metadata. Now also carries a
   `detailHref(id)` builder so ticker + Recent Activity items deep-link
   to the specific record instead of the generic category page. When
   the platform has no per-record admin route (e.g. individual messages
   or appointments), `detailHref` returns null and callers fall back to
   the category `href`. */
const SOURCE_META = {
    lead:            { icon: UserList,  tone: "amber",   href: "/admin/leads?tab=web",         detailHref: (id) => id ? `/admin/leads/web/${id}` : null },
    message:         { icon: Envelope,  tone: "amber",   href: "/admin/messages",              detailHref: () => null },
    application:     { icon: Briefcase, tone: "primary", href: "/admin/leads?tab=application", detailHref: (id) => id ? `/admin/leads/application/${id}` : null },
    appointment:     { icon: Calendar,  tone: "primary", href: "/admin/appointments",          detailHref: () => null },
    "patient-signup":{ icon: UserPlus,  tone: "primary", href: "/admin/patients",              detailHref: (id) => id ? `/admin/patients/${id}` : null },
};

function relativeTime(iso) {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function formatHours(h) {
    if (h === null || h === undefined) return "—";
    if (h < 24) return `${h.toFixed(1)}h`;
    return `${(h / 24).toFixed(1)}d`;
}

/* iter100.244 — The old `SectionHeader` primitive was superseded by
   the shared `<AdminSectionShell>` component so every section now uses
   the same Analytics-style card grammar. `SectionHeader` deleted. */

/* iter100.241 — TAT tiles are drill-downs now. Each tile that represents
   an enumerable set of cases (Planner SLA, Doctor review, Production
   pickup, Manufacturing) accepts an `href` and renders as a <Link> to
   the pre-filtered Cases page. Revision rate remains a static tile —
   it's a computed rate, not a set of records. */
function TatMetric({ label, sublabel, hours, sample, tone = "primary", testid, href }) {
    const inner = (
        <>
            <Clock size={16} weight="duotone" className={TONE_ICON[tone] || TONE_ICON.primary} />
            <div className="font-display text-2xl font-bold mt-2 tabular-nums">{formatHours(hours)}</div>
            <div className="text-xs font-medium">{label}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
                {sublabel}{sample ? ` · n=${sample}` : " · no data"}
            </div>
        </>
    );
    if (href) {
        return (
            <Link
                to={href}
                className="p-4 bg-secondary/40 rounded-xl block hover:bg-secondary/70 hover:ring-1 hover:ring-primary/30 transition-all group"
                data-testid={testid}
            >
                {inner}
            </Link>
        );
    }
    return (
        <div className="p-4 bg-secondary/40 rounded-xl" data-testid={testid}>
            {inner}
        </div>
    );
}

function ReviewQueueTile({ to, Icon, label, sub, count, testid }) {
    const hasWork = count > 0;
    return (
        <Link
            to={to}
            className={`group flex items-center gap-3 p-3 rounded-xl border transition-colors ${hasWork ? "border-amber-200 bg-amber-50/60 hover:bg-amber-50" : "border-border bg-secondary/40 hover:bg-secondary/60"}`}
            data-testid={testid}
        >
            <div className={`w-10 h-10 rounded-lg grid place-items-center ${hasWork ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                <Icon size={18} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{label}</div>
                <div className="text-[11px] text-muted-foreground truncate">{sub}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <span className={`min-w-[28px] h-7 px-2 rounded-full text-sm font-bold tabular-nums grid place-items-center ${hasWork ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                    {count}
                </span>
                <ArrowRight size={12} weight="bold" className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </Link>
    );
}

export default function AdminOverview() {
    const [stats, setStats] = useState({});
    const [tat, setTat] = useState(null);
    const [reviewQueue, setReviewQueue] = useState(null);
    const [dataHealth, setDataHealth] = useState(null);
    const [loading, setLoading] = useState(true);

    /* iter101.26 — Dashboard was overcrowded with 9 stacked sections.
       Split into 3 focused tabs so admins land on the "Pulse" (today's
       operational surface) by default and drill into Throughput /
       Provider Health only when they need to. Tab state deep-links via
       ?tab= so bookmarks + refreshes preserve the view — same pattern
       already used on /admin/analytics. */
    const VALID_TABS = ["pulse", "throughput", "providers"];
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = (() => {
        const t = searchParams.get("tab");
        return VALID_TABS.includes(t) ? t : "pulse";
    })();
    const [activeTab, setActiveTabState] = useState(initialTab);
    const setActiveTab = (t) => {
        setActiveTabState(t);
        const next = new URLSearchParams(searchParams);
        if (t === "pulse") next.delete("tab");
        else next.set("tab", t);
        setSearchParams(next, { replace: true });
    };

    /* iter100.247 — Recent activity is now live via SSE. Was previously
       a one-shot GET /admin/activity on mount that went stale as the
       page aged. Now the section and the ticker share the exact same
       hook, so an admin who tabs away for 20 minutes returns to a
       Recent Activity section that has been silently keeping itself
       up to date. */
    const { items: streamedActivity, connected: activityLive } = useAdminActivityStream(15);
    const activity = streamedActivity || [];

    useEffect(() => {
        Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/case-tat"),
            api.get("/admin/review-queue/summary"),
            api.get("/admin/doctor-data-health"),
        ]).then(([s, t, rq, dh]) => {
            setStats(s.data);
            setTat(t.data);
            setReviewQueue(rq.data);
            setDataHealth(dh.data);
        }).finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="admin-overview" className="space-y-6">
            {/* iter100.190 — Page header unified with Doctor Dashboard
                grammar: text-2xl H1 + text-xs subtitle. "CONTROL CENTER"
                overline eyebrow removed. */}
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Overview</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Operations Overview</h1>
                <p className="text-xs text-muted-foreground mt-1">Live across the Invisique™ platform.</p>
            </div>

            {/* iter100.240 — Live activity ticker. SSE stream, marquee
                right→left, pause on hover, deep-links per item. Sits
                above the KPI cards for immediate "platform is alive"
                signal on every page load. */}
            <AdminTicker />

            {/* iter101.26 — Sub-dashboard tabs to split the previously
                overcrowded overview into 3 focused surfaces:
                  · Pulse       — today's operational actions
                  · Throughput  — case velocity + referral pipeline
                  · Providers   — doctor microsite health + adoption
                Same tab pattern as /admin/analytics for consistency. */}
            <div className="border-b border-border" data-testid="admin-overview-tabs">
                <nav className="flex gap-6" aria-label="Overview sections">
                    {[
                        { id: "pulse",      label: "Pulse",       note: "Live cases · Review queue" },
                        { id: "throughput", label: "Throughput",  note: "Case TAT · Referrals · Quality" },
                        { id: "providers",  label: "Provider health", note: "Data gaps · Services · Compliance" },
                    ].map((t) => {
                        const isActive = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                data-testid={`admin-overview-tab-${t.id}`}
                                className={`relative pb-3 pt-1 text-left transition-colors ${
                                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                <div className="text-[13px] font-bold">{t.label}</div>
                                <div className="text-[10.5px] text-muted-foreground/80 mt-0.5">{t.note}</div>
                                {isActive && (
                                    <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary rounded-t" />
                                )}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {activeTab === "pulse" && (<>
            {/* iter100.243 — KPI Snapshot section using the shared
                AdminSectionShell so the header grammar exactly matches
                every other section on this page. 12 tiles across 3
                lenses (Acquisition · Community · Product). Each cell
                is a genuine drill-down link. */}
            <AdminSectionShell
                eyebrow="Platform pulse"
                tone="teal"
                description="Live counts across every product surface."
                right={
                    <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        {loading ? "Loading…" : "12 metrics"}
                    </div>
                }
                testid="admin-kpi-snapshot"
            >
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-px bg-border">
                    {STAT_CARDS.map((s) => (
                        <Link
                            to={s.href}
                            key={s.key}
                            className="group px-4 py-3 bg-card hover:bg-secondary/50 transition-colors block"
                            data-testid={`admin-stat-${s.key}`}
                        >
                            <div className="flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground" title={s.label}>
                                <s.icon size={12} weight="duotone" className={TONE_ICON[s.tone] || TONE_ICON.primary} />
                                <span className="truncate">{s.label}</span>
                            </div>
                            <div className="mt-1 font-display text-[22px] font-bold tabular-nums leading-none">
                                {loading ? <span className="inline-block h-4 w-8 bg-secondary rounded animate-pulse" /> : (stats[s.key] ?? 0)}
                            </div>
                            <div className="text-[10.5px] text-muted-foreground mt-1 truncate">{s.sub}</div>
                        </Link>
                    ))}
                </div>
            </AdminSectionShell>

            {/* iter101.92 — Compliance coach missing alert. Only renders
                when there are cases that opted-in for CoE-Support and
                have reached the "route-to-production" gate window
                (approved_pending_mfg or later) without a coach assigned.
                Prevents the "route blocked at the last second" surprise
                the Option 2 hard gate would otherwise create. */}
            <ComplianceCoachMissingBanner />

            {/* iter101.25 — Aligner Cases Pipeline. Every case status +
                count in one glanceable strip so admins spot pileups
                (e.g., "12 cases sitting in QA bounced") without opening
                the Cases tab. Each tile deep-links to /admin/cases with
                the matching status filter preselected. Auto-refreshes
                every 60s (iter101.26). */}
            <AlignerCasesPipeline />

            {/* iter100.244 — Awaiting your review. Now wrapped in the
                shared AdminSectionShell (amber tone since these items
                are pending work). Body still uses its own p-4 amber-
                bordered content zone via bodyPad. */}
            {reviewQueue && reviewQueue.total > 0 && (
                <AdminSectionShell
                    eyebrow="Awaiting your review"
                    tone="teal"
                    description={`${reviewQueue.total} item${reviewQueue.total === 1 ? "" : "s"} awaiting review across three queues.`}
                    right={
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-amber-100 text-amber-800">
                            SLA · &lt; 24h
                        </span>
                    }
                    bodyPad
                    testid="admin-review-queue-card"
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <ReviewQueueTile
                            to="/admin/case-validations"
                            Icon={ClipboardText}
                            label="Case validations"
                            sub="Doctor case write-ups"
                            count={reviewQueue.case_validations}
                            testid="overview-review-cases"
                        />
                        <ReviewQueueTile
                            to="/admin/course-reviews"
                            Icon={GraduationCap}
                            label="Course submissions"
                            sub="Cert-Author drafts"
                            count={reviewQueue.course_reviews}
                            testid="overview-review-courses"
                        />
                        <ReviewQueueTile
                            to="/admin/blog?tab=review"
                            Icon={Newspaper}
                            label="Blog posts"
                            sub="Awaiting publish"
                            count={reviewQueue.blog_reviews}
                            testid="overview-review-blogs"
                        />
                    </div>
                </AdminSectionShell>
            )}
            </>)}

            {activeTab === "throughput" && (<>
            {/* iter100.164 — Portal Quality Metrics widget. Uses the
                shared AdminSectionShell (updated in iter100.244). */}
            <PlatformQualityWidget />

            {/* iter100.244 — Case turnaround. Wrapped in shared shell. */}
            {tat && tat.total_cases_tracked > 0 && (
                <AdminSectionShell
                    eyebrow="Case turnaround"
                    tone="teal"
                    description="Average time each stage takes across the platform."
                    right={
                        <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums">
                            {tat.total_cases_tracked} {tat.total_cases_tracked === 1 ? "case" : "cases"} tracked
                        </span>
                    }
                    bodyPad
                    testid="admin-tat-panel"
                >
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <TatMetric label="Planner SLA"       sublabel="Submit → Ready"      hours={tat.planner_sla_hours}      sample={tat.planner_sample}     tone="primary" testid="tat-planner"    href="/admin/cases?bucket=planning&from=overview-tat" />
                        <TatMetric label="Doctor review"     sublabel="Ready → Approve"     hours={tat.doctor_review_hours}    sample={tat.doctor_sample}      tone="primary" testid="tat-doctor"     href="/admin/cases?bucket=with_doctor&from=overview-tat" />
                        <TatMetric label="Production pickup" sublabel="Approve → Production" hours={tat.production_pickup_hours} sample={tat.production_sample}  tone="amber"   testid="tat-production" href="/admin/cases?bucket=awaiting_assignment&from=overview-tat" />
                        <TatMetric label="Manufacturing"     sublabel="Production → Ship"   hours={tat.shipping_hours}         sample={tat.shipping_sample}    tone="primary" testid="tat-shipping"   href="/admin/cases?bucket=production&from=overview-tat" />
                        <div className="p-4 bg-secondary/40 rounded-xl" data-testid="tat-revision-rate">
                            <Clock size={16} weight="duotone" className="text-rose-600" />
                            <div className="font-display text-2xl font-bold mt-2 tabular-nums">{tat.revision_rate ?? 0}%</div>
                            <div className="text-xs font-medium">Revision rate</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                Cases sent back for changes
                            </div>
                        </div>
                    </div>
                </AdminSectionShell>
            )}

            {/* iter100.203 — Referral pipeline widget (replaces the old
                DemoLinkActivityWidget). Reads /api/admin/referrals/kpis
                and deep-links into the unified referrals hub. */}
            <ReferralPipelineWidget />
            </>)}

            {activeTab === "providers" && (<>
            {/* iter100.244 — Doctor data health wrapped in shared shell.
                Amber tone since every populated row is booking-conversion
                risk that admin should chase.
                iter100.245 — Right slot upgraded from a bare Warning
                icon to a real count (sum of all gap types) so the
                section grammar matches Platform Pulse / Case Turnaround. */}
            {dataHealth && dataHealth.total_doctors > 0 && (() => {
                const totalGaps = Object.values(dataHealth.checks || {}).reduce((sum, v) => sum + (v || 0), 0);
                return (
                <AdminSectionShell
                    eyebrow="Doctor data health"
                    tone="teal"
                    description={`Coverage gaps on public microsites across ${dataHealth.total_doctors} active provider${dataHealth.total_doctors === 1 ? "" : "s"}.`}
                    right={
                        <span
                            className={`text-[10.5px] font-semibold uppercase tracking-[0.16em] tabular-nums ${totalGaps > 0 ? "text-amber-700" : "text-muted-foreground"}`}
                            data-testid="admin-data-health-gap-count"
                        >
                            {totalGaps > 0 ? `${totalGaps} coverage gap${totalGaps === 1 ? "" : "s"}` : "No gaps"}
                        </span>
                    }
                    bodyPad
                    testid="admin-doctor-data-health"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                            { k: "no_photo",              label: "No photo",             hint: "Public listing shows placeholder avatar" },
                            { k: "no_bio",                label: "No bio",               hint: "\"About Dr. X\" section renders empty" },
                            { k: "no_business_hours",     label: "No business hours",    hint: "Microsite shows \"Hours by appointment\"" },
                            { k: "partial_business_hours",label: "Partial hours",        hint: "Some days missing open/close (legacy corruption)" },
                            { k: "no_consultation_fee",   label: "No consultation fee",  hint: "Sidebar shows blank fee — kills trust" },
                            { k: "no_specialization",     label: "No specialization",    hint: "Search filters can't match this doctor" },
                            { k: "no_qualifications",     label: "No qualifications",    hint: "Hero credentials line hidden" },
                            { k: "no_languages",          label: "No languages",         hint: "Multilingual chip hidden" },
                            { k: "no_landing_slug",       label: "No landing slug",      hint: "Public microsite URL unavailable" },
                            { k: "no_clinic_name",        label: "No clinic name",       hint: "\"Consult at\" sidebar blank" },
                            { k: "not_onboarded",         label: "Not onboarded",        hint: "Never completed the setup wizard" },
                        ].map((row) => {
                            const count = dataHealth.checks?.[row.k] ?? 0;
                            const hasWork = count > 0;
                            const inner = (
                                <>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">{row.label}</div>
                                        <span className={`min-w-[28px] h-6 px-2 rounded-full text-xs font-bold tabular-nums grid place-items-center ${hasWork ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                                            {count}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{row.hint}</div>
                                </>
                            );
                            /* iter100.252 — Neutralised the base state so the section
                               reads like Platform Pulse. Amber tint reveals only on
                               hover. The count badge itself stays amber so admins can
                               still spot problems at a glance without hovering. */
                            const shared = `p-3 rounded-xl border ${hasWork ? "border-border bg-card hover:bg-amber-50/60 hover:border-amber-300 transition-colors" : "border-border bg-secondary/40"}`;
                            return hasWork ? (
                                <Link
                                    key={row.k}
                                    to={`/admin/providers?gap=${row.k}`}
                                    className={shared + " group"}
                                    data-testid={`data-health-${row.k}`}
                                >
                                    {inner}
                                </Link>
                            ) : (
                                <div key={row.k} className={shared} data-testid={`data-health-${row.k}`}>
                                    {inner}
                                </div>
                            );
                        })}
                    </div>
                </AdminSectionShell>
                );
            })()}

            {/* iter100.239 — Services adoption widget. Sits right before
                the Practice Compliance Heatmap so admins see the
                "adoption pulse" of every Invisique service before drilling
                into per-doctor compliance drift. Fully independent from
                the heatmap — different metric, different actor. */}
            <ServicesAdoptionWidget />

            {/* Practice compliance heatmap — external component, own grammar,
                out of scope for this sweep. Wrapping tag preserved so the
                spacing rhythm above and below stays consistent. */}
            <AdminComplianceHeatmap />
            </>)}

            {/* iter101.25 — Recent Activity was moved out of Overview
                into its own sidebar tab (Insights → Activity Feed).
                Keeps this dashboard focused on TODAY's actionable work;
                the activity log is a spelunking tool that deserves its
                own dedicated screen. */}
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   ComplianceCoachMissingBanner — iter101.92
   ---------------------------------------------------------------------
   Warns the admin when a case that opted-in for "Compliance Managed by
   Invisique" has reached (or passed) `approved_pending_mfg` without a
   PES / compliance coach assigned. This is the visibility half of
   Option 2 — the hard gate on `/admin/cases/{id}/assign-production`
   will refuse the routing, but we want the admin to see it coming from
   the dashboard, not discover it at the Route CTA.

   Silent when there's nothing to do (returns null). Auto-refreshes with
   the same 60s cadence as the pipeline strip so a newly-flipped case
   shows up on its own.
   ───────────────────────────────────────────────────────────────────── */
const RELEVANT_STATUSES_FOR_COACH_ALERT = new Set([
    "approved_pending_mfg",
    "awaiting_production_assignment",
    "submitted_to_production",
]);

function ComplianceCoachMissingBanner() {
    const [pending, setPending] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const pollRef = React.useRef(null);

    const load = React.useCallback(async () => {
        try {
            const { data } = await api.get("/admin/coe-compliance/cases", { params: { assigned: "false" } });
            const flagged = (data || []).filter((c) => RELEVANT_STATUSES_FOR_COACH_ALERT.has(c.status));
            setPending(flagged);
        } catch {
            setPending([]);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        load();
        pollRef.current = setInterval(load, 60_000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [load]);

    if (loading || pending.length === 0) return null;

    const n = pending.length;
    const preview = pending.slice(0, 3);
    return (
        <div
            className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
            data-testid="admin-coach-missing-banner"
        >
            <div className="w-9 h-9 rounded-xl bg-rose-100 border border-rose-200 grid place-items-center shrink-0">
                <Warning size={18} weight="fill" className="text-rose-700" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-rose-900" data-testid="admin-coach-missing-title">
                    {n} case{n === 1 ? "" : "s"} need a compliance coach before routing to production
                </div>
                <div className="text-[12px] text-rose-800/90 mt-0.5 leading-relaxed">
                    The doctor opted for &ldquo;Compliance Managed by Invisique&rdquo; but no PES is assigned yet. Route-to-production will be blocked until you allocate one from the case&apos;s Overview panel.
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {preview.map((c) => (
                        <Link
                            key={c.id}
                            to={`/admin/cases/${c.id}?tab=overview`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-800 hover:text-rose-900 bg-white/70 border border-rose-200 rounded-full px-2 py-0.5 tabular-nums"
                            title={c.patient_name || c.code || c.id}
                            data-testid={`admin-coach-missing-case-${c.id}`}
                        >
                            {c.code || c.patient_name || c.id?.slice(0, 6)}
                            <ArrowRight size={10} weight="bold" />
                        </Link>
                    ))}
                    {n > preview.length && (
                        <span className="text-[11px] text-rose-800/80 font-semibold">
                            +{n - preview.length} more
                        </span>
                    )}
                    <Link
                        to="/admin/coe/compliance?assigned=false"
                        className="ml-auto inline-flex items-center gap-1 text-[11.5px] font-bold text-rose-900 hover:underline"
                        data-testid="admin-coach-missing-view-all"
                    >
                        Assign coaches
                        <ArrowRight size={11} weight="bold" />
                    </Link>
                </div>
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   AlignerCasesPipeline — iter101.25
   ---------------------------------------------------------------------
   Grouped view of every case status with counts + deep-links to
   /admin/cases?status=X so admins can triage right from the dashboard.

   Grouping mirrors the doctor-side pipeline stages so admins and
   doctors share the same mental model:
     1. Intake         — draft / info_requested / assigned_to_planner
     2. Planning       — in_planning / plan_submitted / plan_qa
     3. With Doctor    — with_doctor / revision_requested
     4. Manufacturing  — approved_pending_mfg / in_production / qc / packaging
     5. Shipping       — shipped / delivered
     6. Treatment      — in_treatment / refinement_requested
     7. Terminal       — completed / cancelled
   ───────────────────────────────────────────────────────────────────── */
function AlignerCasesPipeline() {
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    /* iter101.26 — 60s auto-refresh so admins spot pipeline pileups in
       real time without needing to reload the whole dashboard. Poll is
       cheap (one aggregation query) so we hit the endpoint every minute
       and re-render only if `data` actually changed on the wire. Silent
       polls skip the "Loading…" spinner so the strip doesn't flicker. */
    const pollRef = React.useRef(null);
    const [lastRefreshedAt, setLastRefreshedAt] = React.useState(null);

    const load = React.useCallback((opts = { silent: false }) => {
        if (!opts.silent) setLoading(true);
        return api.get("/admin/cases/pipeline-summary")
            .then((r) => {
                setData(r.data);
                setLastRefreshedAt(new Date());
            })
            .catch(() => { if (!opts.silent) setData({ groups: [], total: 0 }); })
            .finally(() => { if (!opts.silent) setLoading(false); });
    }, []);

    React.useEffect(() => {
        load({ silent: false });
        pollRef.current = setInterval(() => load({ silent: true }), 60_000);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [load]);

    const groups = data?.groups || [];
    const total = data?.total || 0;
    const actionable = data?.actionable || 0;

    return (
        <AdminSectionShell
            eyebrow="Aligner Cases Pipeline"
            tone="indigo"
            description="Every case across every status. Click any tile to jump into the filtered case list."
            right={
                <div className="flex items-center gap-3">
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-muted-foreground tabular-nums" data-testid="admin-pipeline-total">
                        {loading ? "Loading…" : `${total} live case${total === 1 ? "" : "s"}`}
                    </span>
                    {/* iter101.26 — LIVE chip. Small pulsing dot to signal
                        the strip refreshes itself in the background so
                        admins trust the numbers are current. */}
                    {!loading && (
                        <span
                            className="inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200/70"
                            data-testid="admin-pipeline-live-chip"
                            title={lastRefreshedAt ? `Last refreshed ${lastRefreshedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "Auto-refreshes every 60s"}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            LIVE
                        </span>
                    )}
                    {actionable > 0 && (
                        <span
                            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-amber-50 text-amber-800"
                            data-testid="admin-pipeline-actionable"
                            title="Cases where admin action is needed right now"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                            {actionable} need you
                        </span>
                    )}
                    <Link
                        to="/admin/cases"
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-600 transition-colors group"
                        data-testid="admin-pipeline-view-all"
                    >
                        Open cases
                        <ArrowRight size={11} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                </div>
            }
            bodyPad
            testid="admin-cases-pipeline"
        >
            {loading ? (
                <div className="text-muted-foreground text-sm py-4">Loading pipeline…</div>
            ) : total === 0 ? (
                <div className="text-muted-foreground text-sm py-4">No cases yet — this section will populate once doctors start submitting.</div>
            ) : (
                <div className="space-y-4">
                    {groups.map((g) => (
                        <PipelineGroupRow key={g.key} group={g} />
                    ))}
                </div>
            )}
        </AdminSectionShell>
    );
}


function PipelineGroupRow({ group }) {
    return (
        <div data-testid={`admin-pipeline-group-${group.key}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    {group.label}
                </div>
                <div className="text-[11px] text-muted-foreground tabular-nums">
                    {group.total} case{group.total === 1 ? "" : "s"}
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {group.statuses.map((s) => (
                    <PipelineStatusTile key={s.status} status={s} />
                ))}
            </div>
        </div>
    );
}


function PipelineStatusTile({ status }) {
    const tone = status.needs_admin
        ? "bg-amber-50 border-amber-200 hover:border-amber-300 hover:bg-amber-100 text-amber-900"
        : status.tone === "rose"
            ? "bg-rose-50 border-rose-200 hover:border-rose-300 hover:bg-rose-100 text-rose-900"
            : status.tone === "emerald"
                ? "bg-emerald-50 border-emerald-200 hover:border-emerald-300 hover:bg-emerald-100 text-emerald-900"
                : status.tone === "sky"
                    ? "bg-sky-50 border-sky-200 hover:border-sky-300 hover:bg-sky-100 text-sky-900"
                    : status.tone === "violet"
                        ? "bg-violet-50 border-violet-200 hover:border-violet-300 hover:bg-violet-100 text-violet-900"
                        : "bg-secondary/50 border-border hover:border-primary/30 text-foreground";
    return (
        <Link
            to={`/admin/cases?status=${encodeURIComponent(status.status)}`}
            className={`rounded-lg border px-3 py-2.5 transition-all ${tone}`}
            data-testid={`admin-pipeline-status-${status.status}`}
            title={status.description || status.label}
        >
            <div className="flex items-baseline justify-between gap-2">
                <div className="text-[11.5px] font-semibold truncate">{status.label}</div>
                <div className="font-display text-lg font-bold tabular-nums shrink-0">{status.count}</div>
            </div>
            {status.needs_admin && (
                <div className="text-[9.5px] font-bold uppercase tracking-wider mt-1 inline-flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-amber-500" />
                    Admin action
                </div>
            )}
        </Link>
    );
}

